"use client";

import { useRef, useState } from "react";
import { useI18n } from "@/components/i18n-provider";
import type { TalentPoolUploadChoice } from "@/lib/indexed-db";
import {
  reviewLanguageForLocale,
  type ReviewLanguage,
} from "@/lib/cv/review-language";
import { trackEvent } from "@/lib/analytics";
import {
  trackCvUploadSubmitFailed,
  trackCvUploadSuccess,
  trackCvUploadValidationFailed,
} from "@/components/cv-form/cv-upload-form-analytics";

export type Purpose = "job" | "scholarship";

export interface CvUploadMobilePayload {
  file: File;
  experienceLevel: string;
  reviewPurpose: Purpose;
  targetJobTitle: string;
  programCampus: string;
  jobDescription: string;
  allowTalentPool: boolean;
  talentPoolUploadChoice: TalentPoolUploadChoice;
  reviewLanguage: ReviewLanguage;
}

const MAX_FILE_SIZE_BYTES = 10 * 1024 * 1024;
const ANALYTICS_SOURCE = "cv_upload_form_mobile";

interface UseCvUploadFormMobileOptions {
  onUpload: (payload: CvUploadMobilePayload) => Promise<void>;
  isUploading: boolean;
  isSignedIn: boolean;
}

export function useCvUploadFormMobile({
  onUpload,
  isUploading,
  isSignedIn,
}: UseCvUploadFormMobileOptions) {
  const { dictionary, locale } = useI18n();
  const [file, setFile] = useState<File | null>(null);
  const [experienceLevel, setExperienceLevel] = useState<string>("");
  const [error, setError] = useState<string>("");
  const [attemptedSubmit, setAttemptedSubmit] = useState(false);
  const [purpose, setPurpose] = useState<Purpose | "">("");
  const [showTargetingFields, setShowTargetingFields] = useState(false);
  const [targetJobTitle, setTargetJobTitle] = useState("");
  const [programCampus, setProgramCampus] = useState("");
  const [jobDescription, setJobDescription] = useState("");
  const [talentPoolChoice, setTalentPoolChoice] =
    useState<TalentPoolUploadChoice | null>(null);
  const [reviewLanguage, setReviewLanguage] = useState<ReviewLanguage>(() =>
    reviewLanguageForLocale(locale),
  );
  const fileInputRef = useRef<HTMLInputElement>(null);
  const purposeRef = useRef<HTMLButtonElement>(null);
  const experienceRef = useRef<HTMLButtonElement>(null);

  const fieldErrors = dictionary.cvUploadForm.errors;

  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const selectedFile = event.target.files?.[0];
    if (selectedFile) {
      if (selectedFile.type !== "application/pdf") {
        setError(fieldErrors.pdfOnly);
        trackCvUploadValidationFailed(ANALYTICS_SOURCE, "file_type");
        return;
      }

      if (selectedFile.size > MAX_FILE_SIZE_BYTES) {
        setError(fieldErrors.fileSize);
        trackCvUploadValidationFailed(ANALYTICS_SOURCE, "file_size");
        return;
      }

      setFile(selectedFile);
      setError("");
    }
  };

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    setAttemptedSubmit(true);

    if (!file || !experienceLevel || !purpose || !talentPoolChoice) {
      setError(fieldErrors.missingFields);
      trackCvUploadValidationFailed(ANALYTICS_SOURCE, "missing_fields");
      requestAnimationFrame(() => {
        if (!file) {
          fileInputRef.current?.focus();
        } else if (!purpose) {
          purposeRef.current?.focus();
        } else if (!experienceLevel) {
          experienceRef.current?.focus();
        } else {
          document.getElementById("mobile-talent-pool-yes")?.focus();
        }
      });
      return;
    }

    if (!isSignedIn) {
      trackEvent("sign_up_click", { context: ANALYTICS_SOURCE });
    }

    setError("");

    // Track CV upload attempt
    trackEvent("cv_upload_click", {
      purpose: purpose as Purpose,
      experience_level: experienceLevel,
      has_target_job: Boolean(targetJobTitle.trim()),
      has_job_description: Boolean(jobDescription.trim()),
      allow_talent_pool: talentPoolChoice === "yes",
      source: "hero",
      authed: isSignedIn,
    });

    try {
      await onUpload({
        file,
        experienceLevel,
        reviewPurpose: purpose as Purpose,
        targetJobTitle,
        programCampus,
        jobDescription,
        allowTalentPool: talentPoolChoice === "yes",
        talentPoolUploadChoice: talentPoolChoice,
        reviewLanguage,
      });

      // Signed-out users are redirected to OAuth here; the actual upload
      // happens later on the pending page, which tracks its own outcome.
      if (isSignedIn) {
        trackCvUploadSuccess(ANALYTICS_SOURCE);
      }
    } catch (err) {
      trackCvUploadSubmitFailed(ANALYTICS_SOURCE, err);
      setError(err instanceof Error ? err.message : fieldErrors.uploadFailed);
    }
  };

  return {
    dictionary,
    isSignedIn,
    file,
    experienceLevel,
    setExperienceLevel,
    error,
    attemptedSubmit,
    purpose,
    setPurpose,
    showTargetingFields,
    toggleTargetingFields: () =>
      setShowTargetingFields((current) => !current),
    targetJobTitle,
    setTargetJobTitle,
    programCampus,
    setProgramCampus,
    jobDescription,
    setJobDescription,
    talentPoolChoice,
    setTalentPoolChoice,
    reviewLanguage,
    setReviewLanguage,
    fileInputRef,
    purposeRef,
    experienceRef,
    requiredMessages: {
      file: fieldErrors.fileRequired,
      purpose: fieldErrors.purposeRequired,
      experience: fieldErrors.experienceRequired,
    },
    // Keep the CTA clickable so handleSubmit can run validation and surface the
    // per-field hints; only an in-flight upload disables it.
    isSubmitDisabled: isUploading,
    handleFileChange,
    handleSubmit,
  };
}
